import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { ShieldCheck, FileCheck2, Music4, Building2, Calculator, Headphones, Phone, MessageCircle } from "lucide-react";
import LicenseCalculator from "@/components/LicenseCalculator";

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(900px 320px at 80% 30%, rgba(212,255,0,0.18), transparent 70%), radial-gradient(700px 400px at 10% 90%, rgba(212,255,0,0.06), transparent 70%)",
          }}
        />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:py-28 lg:px-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A23A]" data-testid="hero-tag">
              {t("hero.tag")}
            </p>
            <h1 className="mt-4 font-display text-4xl font-black leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-7xl">
              {t("hero.title1")}
              <br />
              <span className="text-[#D4A23A]">{t("hero.title2")}</span>
            </h1>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-zinc-400 sm:text-base">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#calculator"
                data-testid="hero-cta-calc"
                className="rounded-full bg-[#D4A23A] px-6 py-3 text-sm font-semibold text-black transition hover:bg-[#E2B85C]"
              >
                {t("hero.cta1")}
              </a>
              <Link
                to="/music"
                data-testid="hero-cta-catalog"
                className="rounded-full border border-white/15 px-6 py-3 text-sm text-white transition hover:bg-white/5"
              >
                {t("nav.musicAccess")}
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-white/5 pt-6 text-xs">
              {[
                ["10+", "categorii activitate"],
                ["3-12", "luni durată"],
                ["100%", "licențiat legal"],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="font-display text-2xl font-black text-[#D4A23A]">{k}</p>
                  <p className="mt-0.5 text-zinc-500">{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — feature cards stacked */}
          <div className="grid gap-4">
            {[
              { icon: ShieldCheck, title: "Licență oficială", body: "Certificat PDF cu număr unic, valabil pentru locația și suprafața declarate." },
              { icon: Calculator, title: "Preț transparent", body: "Calculator instant — categorie × m² × durată. Fără surprize, fără abonamente lunare obligatorii." },
              { icon: Headphones, title: "Streaming continuu", body: "Player web optimizat. Niciun fișier descărcat — siguranță și conformitate maximă." },
            ].map(({ icon: Icon, title, body }, i) => (
              <div
                key={i}
                className="group rounded-2xl border border-white/5 bg-[#121212] p-6 transition hover:border-white/15"
                data-testid={`feature-card-${i}`}
              >
                <div className="flex items-center gap-4">
                  <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#D4A23A]/10 text-[#D4A23A] transition group-hover:bg-[#D4A23A] group-hover:text-black">
                    <Icon size={18} />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold">{title}</h3>
                    <p className="mt-1 text-sm text-zinc-400">{body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-white/5 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 md:grid-cols-4">
            {[
              { icon: Building2, n: "01", t: "Cont business", d: "Înregistrezi datele firmei și ale locației." },
              { icon: Calculator, n: "02", t: "Calculator", d: "Alegi categoria, suprafața și durata." },
              { icon: FileCheck2, n: "03", t: "Plată online", d: "Plătești securizat cu cardul, primești documentele după confirmare." },
              { icon: Music4, n: "04", t: "Streaming activ", d: "Certificat PDF + acces complet la catalog." },
            ].map(({ icon: Icon, n, t, d }) => (
              <div key={n} className="space-y-3">
                <span className="font-mono text-xs text-[#D4A23A]">{n}</span>
                <Icon className="text-white" size={26} strokeWidth={1.5} />
                <h3 className="font-display text-xl font-bold">{t}</h3>
                <p className="text-sm text-zinc-400">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section id="calculator" className="border-b border-white/5 py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A23A]">{t("pricing.heading")}</p>
            <h2 className="mt-3 font-display text-3xl font-black sm:text-5xl">{t("pricing.heading")}</h2>
            <p className="mt-3 text-sm text-zinc-400">{t("pricing.sub")}</p>
          </div>
          <LicenseCalculator />
        </div>
      </section>

      <footer className="py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 text-xs text-zinc-500 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="tel:+40770890286"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-zinc-300 transition hover:border-[#D4A23A]/60 hover:text-[#D4A23A]"
              aria-label="Sună la 0770 890 286"
            >
              <Phone size={15} />
              0770 890 286
            </a>
            <a
              href="https://wa.me/40770890286"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-zinc-300 transition hover:border-[#D4A23A]/60 hover:text-[#D4A23A]"
              aria-label="Contactează StockMusic pe WhatsApp"
            >
              <MessageCircle size={15} />
              WhatsApp
            </a>
          </div>
          <div className="flex w-full flex-col items-center justify-between gap-3 sm:flex-row">
            <p>© {new Date().getFullYear()} StockMusic Licensing. Toate drepturile rezervate.</p>
            <p>Streaming licențiat — fără descărcare, fără redistribuire.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
